import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormRow } from '@tanium/react-form';
import { Button } from '@tanium/react-button';
import { Box } from '@tanium/react-box';
import { Select, ValueType } from '@tanium/react-select';
import { Stack, StackChild } from '@tanium/react-stack';
import { useDebouncedCallback } from 'use-debounce';
import { LoginFormTextInput, LoginFormButton } from './LoginFormComponents';
import { useAsync, AsyncStatus } from '../../hooks/useAsync';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { useBlockedEmailDomainsHook } from '../../hooks/useBlockedEmailDomains';
import { parseApiErrorMsg } from '../../hooks/useApiErrorMsgParser';
import { ErrorText } from './ErrorText';
import { useOnInputChange } from '../../components/InputHandlerUtils';
import { isValidEmail } from '../../utilities/emailHelper';
import { getDomainSuffix } from '../../utilities/instanceHelper';
import { DomainSuffix } from '../../utilities/constants';
import { Api } from '../../api';
import { errorMsgHandler } from '../../utilities/errorMsgHelper';

export interface CreateAccountProps {
  switchToLocalSignin: () => void;
  switchToCreateAccountConfirm: () => void;
}

type DataRegionSelectOption = { label: string; value: string };

export const CreateAccount: React.VFC<CreateAccountProps> = ({ switchToLocalSignin, switchToCreateAccountConfirm }) => {
  const { t } = useTranslation();

  const dataRegionsSelectOptions: DataRegionSelectOption[] = [
    { label: t('United States (USA)'), value: 'us-e1' },
    { label: t('European Union (EU)'), value: 'eu-n1' },
    { label: t('Japan'), value: 'jp-e1' },
    { label: t('Canada'), value: 'ca-c1' },
    { label: t('United Kingdom (UK)'), value: 'uk-s1' },
    { label: t('Australia'), value: 'au-e1' },
    { label: t('South America'), value: 'sa-e1' },
  ];

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [autofillUsername, setAutofillUsername] = useState(true);
  const [identifier, setIdentifier] = useState('');
  const [selectedDataRegion, setSelectedDataRegion] = React.useState<DataRegionSelectOption>(
    dataRegionsSelectOptions[0]
  );
  const [autofillIdentifier, setAutofillIdentifier] = useState(true);
  const [emailValidationErr, setEmailValidationErr] = useState<string | undefined>();
  const [validationErr, setValidationErr] = useState<string | undefined>();
  const { loadBlockedEmailDomains, blockedEmailDomainsTable } = useBlockedEmailDomainsHook();

  useAsyncEffect(loadBlockedEmailDomains, []);

  const {
    status: createTenantStatus,
    execute: createTenantExecute,
    reset: createTenantReset,
    error: createTenantError,
  } = useAsync(async () => {
    const region = selectedDataRegion.value;
    return Api.tenants.createTenant(email, `${username}@${identifier}.mytanium.com`, identifier, region).then(() => {
      switchToCreateAccountConfirm();
    });
  });

  const isFormValid = useCallback((): boolean => {
    if (!isValidEmail(email)) {
      setEmailValidationErr(t('Invalid email address'));
      return false;
    }
    if (!email.endsWith('@tanium.com') && getDomainSuffix() !== DomainSuffix.PRD) {
      setEmailValidationErr(t('Email must be from @tanium.com'));
      return false;
    }
    if (!identifier.match(/^[A-Za-z0-9]{3,20}$/)) {
      setValidationErr(t('Identifier must be 3-20 characters long and can only contain letters and numbers'));
      return false;
    }

    if (!username.match(/^([a-zA-Z0-9.:/\-_]){1,20}$/)) {
      setValidationErr(
        t('Username must be 1-20 characters long can only contain letters, numbers or one of the following: . : / - _')
      );
      return false;
    }

    setValidationErr(undefined);
    return true;
  }, [t, username, identifier, email]);

  const {
    execute: identifierValidationExecute,
    error: identifierValidationError,
    reset: identifierValidationReset,
  } = useAsync(async () => {
    return Api.tenants.validateIdentifier(identifier).then((resp) => {
      if (!resp.valid) {
        throw new Error(resp.errMsg);
      }
    });
  });

  const usernamePreview = useMemo(() => {
    return `${username}@${identifier.toLowerCase()}.mytanium.com`;
  }, [username, identifier]);

  const debounceIdentifierChange = useDebouncedCallback((newIdentifier: string) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    identifierValidationExecute(newIdentifier);
  }, 300);

  type EmailAddressParts = {
    username: string | undefined;
    domain: string | undefined;
    company: string | undefined;
  };

  const parseEmailAddress = (emailAddress: string): EmailAddressParts => {
    const [emailUser, emailDomain] = emailAddress.split('@');
    let emailCompany;
    if (emailDomain) {
      [emailCompany] = emailDomain.split('.');
    }
    return {
      username: emailUser,
      domain: emailDomain,
      company: emailCompany,
    };
  };

  const validateEmailDomain = (emailDomain: string | undefined): void => {
    if (emailDomain && blockedEmailDomainsTable.has(emailDomain)) {
      setEmailValidationErr(t('The email provider is not allowed'));
    } else {
      setEmailValidationErr(undefined);
    }
  };

  const handleEmailChange = useOnInputChange((newEmail: string) => {
    const emailParts = parseEmailAddress(newEmail);
    validateEmailDomain(emailParts.domain);
    setEmail(newEmail);
    /* istanbul ignore next */
    if (autofillUsername) {
      setUsername(newEmail.split('@')[0]);
    }
    if (autofillIdentifier && emailParts.company) {
      setIdentifier(emailParts.company);
    }
  });

  const handleUsernameChange = useOnInputChange((newUsername: string) => {
    setValidationErr(undefined);
    setUsername(newUsername);
  });

  const handleIdentifierChange = useOnInputChange((newIdentifier: string) => {
    setValidationErr(undefined);
    createTenantReset();
    identifierValidationReset();
    debounceIdentifierChange.callback(newIdentifier);
    setIdentifier(newIdentifier);
  });

  const handleSubmit = useCallback<React.FormEventHandler>(
    async (event) => {
      event.preventDefault();
      if (isFormValid()) {
        await createTenantExecute();
      }
    },
    [createTenantExecute, isFormValid]
  );
  // omitting below because jest won't trigger the onBeforeInput event
  /* istanbul ignore next */
  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <FormRow
          label={t('Email')}
          dataKey="email"
          helpText={t('Enter a valid email to receive Tanium communications.')}
        >
          <LoginFormTextInput value={email} onChange={handleEmailChange} required />
          {emailValidationErr && <ErrorText>{emailValidationErr}</ErrorText>}
        </FormRow>
        <FormRow
          label={t('Identifier')}
          dataKey="identifier"
          helpText={t(
            'Enter an identifier for your account or use the default based upon your email address. Identifiers can contain only letters and numbers.'
          )}
        >
          <LoginFormTextInput
            value={identifier}
            onChange={handleIdentifierChange}
            onBeforeInput={() => {
              // stop autofill of identifier if user has manually typed in identifier field
              setAutofillIdentifier(false);
            }}
            required
          />
          {identifierValidationError && <ErrorText>{errorMsgHandler(identifierValidationError.message)}</ErrorText>}
        </FormRow>
        <FormRow
          label={t('Username Prefix')}
          dataKey="username"
          helpText={t(
            'Enter the username prefix for your user name, or use the default based upon your email address.'
          )}
        >
          <Stack itemSpacing={1} alignItems="center">
            <StackChild grow={100}>
              <LoginFormTextInput
                value={username}
                onChange={handleUsernameChange}
                onBeforeInput={() => {
                  // stop autofill of username if user has manually typed in username field
                  setAutofillUsername(false);
                }}
                required
              />
            </StackChild>
          </Stack>
        </FormRow>
        <FormRow
          label={t('Username Preview')}
          dataKey="usernamePreview"
          helpText={t('Use this username to sign in to the Tanium account portal.')}
        >
          <LoginFormTextInput value={usernamePreview} disabled={true} />
        </FormRow>
        <FormRow
          label={t('Data Region')}
          dataKey="selectedDataRegion"
          helpText={t(
            'The data hosting region where your Tanium Account authentication data will be stored. The data hosting region for Tanium Cloud tenant data is a separate setting and might be a different region. This selection is permanent and cannot be changed later.'
          )}
        >
          <Select
            options={dataRegionsSelectOptions}
            isMulti={false}
            value={selectedDataRegion}
            styles={{ container: (base) => ({ ...base, width: 200 }) }}
            onChange={(valueType: ValueType<DataRegionSelectOption>, _action) => {
              createTenantReset();
              const option = valueType as DataRegionSelectOption;
              setSelectedDataRegion(option);
            }}
          />
        </FormRow>
        {createTenantError && <ErrorText>{parseApiErrorMsg(t, createTenantError)}</ErrorText>}
        {!createTenantError && !identifierValidationError && validationErr && <ErrorText>{validationErr}</ErrorText>}
        <Stack itemSpacing="2" justifyContent="left">
          <LoginFormButton
            loading={createTenantStatus === AsyncStatus.Pending}
            isDisabled={!!createTenantError || !!identifierValidationError || !!validationErr}
          >
            {t('Create Account')}
          </LoginFormButton>
          <Button variant="secondary" onClick={switchToLocalSignin}>
            {t('Cancel')}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
